import React, { useState, useEffect } from "react";
import { styled, keyframes } from "styled-components";
import { Link } from "gatsby";

//assets
import logo from "assets/svg/logo.svg";

// Styles
import colors from "styles/colors.js";
import typographySizes from "styles/typographySizes.js";
import mediaQuery from "styles/mediaQuery.js";
import distances from "styles/distances.js";

// Components
import Btn from "components/global/Btn.js";
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";
import { ContextConsumer } from "src/layouts/Context.js";

// Keyframes for desktop - slide down from top
const slideDownFromTop = keyframes`
  from {
    transform: translateY(-200%);
  }
  to {
    transform: translateY(0);
  }
`;

// Keyframes for mobile - slide up from bottom
const slideUpFromBottom = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const NavWrapper = styled.nav`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background-color: ${colors.white};
  height: 4rem;
  
  /* Combined container styles */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  max-width: 100vw;
  box-sizing: border-box;
  
  /* Desktop animation - slide down from top */
  animation: ${slideDownFromTop} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s both;
  
  &.nav-hidden {
    transform: translateY(-200%);
  }
  
  @media (max-width: ${mediaQuery.tablet}) {
    position: fixed; 
    padding: 1rem;
    top: auto; 
    left: 0; right: 0; bottom: 0;
    /* Mobile animation - slide up from bottom */
    animation: ${slideUpFromBottom} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s both;
  }
`;

const Logo = styled(Link)`
height: 3rem;
width: 6rem;
  line-height: 1;

  img{
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${distances.gapSmall}rem;
  
  @media (max-width: ${mediaQuery.tablet}) {
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    background-color: ${colors.white};
    flex-direction: column;
    padding: 2rem 1rem;
    gap: 2rem;
    align-items: flex-end;
  }
`;

const NavLink = styled(Link)`
  font-size: ${typographySizes.text.small.size.desktop}rem;
  color: ${colors.dark};
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  position: relative;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${colors.blue};
  }
  
  &::after {
    content: "";
    position: absolute;
    bottom: -0.2rem;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${colors.blue};
    transition: width 0.3s ease;
  }
  
  &:hover::after,
  &.active::after {
    width: 100%;
  }
  
  @media (max-width: ${mediaQuery.tablet}) {
    font-size: ${typographySizes.text.body.size.mobile}rem;
    text-align: right;

  }
`;

const ScrollNavLink = styled(ScrollLink)`

font-size: ${typographySizes.text.small.size.desktop}rem;
  color: ${colors.dark};
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  position: relative;
  transition: color 0.3s ease;
  cursor: pointer;
  &:hover {
    color: ${colors.blue};
  }
  
  &::after {
    content: "";
    position: absolute;
    bottom: -0.2rem;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${colors.blue};
    transition: width 0.3s ease;
  }
  
  &:hover::after,
  &.active::after {
    width: 100%;
  }
  
  @media (max-width: ${mediaQuery.tablet}) {
    font-size: ${typographySizes.text.body.size.mobile}rem;
    text-align: right;

  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;

  
  @media (max-width: ${mediaQuery.tablet}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

const MenuBar = styled.span`
  width: 24px;
  height: 2px;
  background-color: ${colors.dark};
  margin: 2px 0;
  transition: 0.3s;
  transform-origin: center;

  &.bar1 {
    transform: ${({ isOpen }) =>
      isOpen ? "rotate(45deg) translate(5px, 5px)" : "none"};
  }
  
  &.bar2 {
    opacity: ${({ isOpen }) => (isOpen ? "0" : "1")};
  }
  
  &.bar3 {
    transform: ${({ isOpen }) =>
      isOpen ? "rotate(-45deg) translate(4px, -4px)" : "none"};
  }
`;

const MobileMenuTextWrapper = styled.div`
  display: none;
  align-items: center;
  gap: 1rem;
  @media (max-width: ${mediaQuery.tablet}) {
  display:flex;
  }

`;

const MobileMenuText = styled.div`
  font-size: ${typographySizes.text.body.size.mobile}rem;
  color: ${colors.dark};
  text-transform: uppercase;

`;

const CTASection = styled.div`
  display: flex;
  align-items: center;
  gap: ${distances.gapSmall}rem;
  
  @media (max-width: ${mediaQuery.tablet}) {
    width: 100%;
    button,
    a, .btn-text-hidden, .btn-text-visible {
      width: 100%;
      text-align: center;
    }
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 999;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  
  @media (min-width: ${mediaQuery.tablet + 1}px) {
    display: none;
  }
`;

const Nav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Navigation items
  const navItems = [
    { label: "O nas ", path: "/o-nas" },
    { label: "Projekty", path: "/projekty" },
    { label: "Co możemy razem?", path: "/co-robimy" },
  ];

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsOpen(!isOpen);
  };

  // Handle scroll blocking when nav is open
  useEffect(() => {
    if (isOpen) {
      // Prevent scrolling
      document.body.style.overflow = "hidden";
    } else {
      // Restore scrolling
      document.body.style.overflow = "unset";
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest("nav")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  return (
    <ContextConsumer>
      {({ data, set }) => (
        <>
          <Overlay isOpen={isOpen} onClick={() => setIsOpen(false)} />
          <NavWrapper
            className={!isVisible ? "nav-hidden" : ""}
            id="nav-wrapper"
          >
            <Logo to="/">
              <img src={logo} alt="Easy Tiger" />
            </Logo>

            <NavLinks isOpen={isOpen}>
              {navItems.map((item, index) => (
                <NavLink
                  activeClassName="active"
                  key={index}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
              <ScrollNavLink
                className="nav-link"
                to="footer"
                spy={true}
                smooth={true}
                offset={0}
                duration={500}
                onClick={() => setIsOpen(false)}
              >
                Kontakt
              </ScrollNavLink>
              <CTASection>
                <Btn
                  color={colors.dark}
                  onClick={() => {
                    set({ contactFormOpen: true });
                    setIsOpen(false); // Close mobile menu
                  }}
                >
                  Zacznij swój projekt
                </Btn>
              </CTASection>
            </NavLinks>

            <MobileMenuTextWrapper>
              <MobileMenuText>MENU</MobileMenuText>
              <MobileMenuToggle onClick={toggleMobileMenu}>
                <MenuBar className="bar1" isOpen={isOpen} />
                <MenuBar className="bar2" isOpen={isOpen} />
                <MenuBar className="bar3" isOpen={isOpen} />
              </MobileMenuToggle>
            </MobileMenuTextWrapper>
          </NavWrapper>
        </>
      )}
    </ContextConsumer>
  );
};

export default Nav;
